import { type Brand } from '@/models/account-management/Brand';
import { brandApi } from '@/api/account-management/brandApi';
﻿import { locationApi } from '@/api/account-management/locationApi';
import { COUNTRIES } from '@/constants/countries';
import { CURRENCIES } from '@/constants/currencies';
import { LOCALES } from '@/constants/locales';
import { CANADIAN_PROVINCES, US_STATES } from '@/constants/states';
import { TIME_ZONES } from '@/constants/timeZones';
import { RestaurantType, type Location, createLocation } from '@/models/account-management/Location';
import { type LocationReadiness } from '@/models/account-management/LocationReadiness';
import { AuthenticationProvider } from '@/models/enums/AuthenticationProvider';
import InfoIcon from '@mui/icons-material/Info';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Chip, Link, Tooltip, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from "../../theme";
import SaveFooter from '../common/SaveFooter';
import Spacer from '../common/Spacer';
import { useAlert } from '../common/alert/AlertProvider';
import { useToast } from '../common/toast/ToastProvider';
import { useCurrentBrand } from '../../hooks/useCurrentBrand';
import { withConfiguredIntegrationsOnly } from '@/models/configuration/untouchedConfig';
import { useSettleDiscard } from '../../hooks/useSettleDiscard';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';
//import SmsIcon from '@mui/icons-material/Sms';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HotelIcon from '@mui/icons-material/Hotel';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import Checkbox from '@mui/material/Checkbox';
import ProviderConfig, { type CopiedConfig } from './ProviderConfig';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CopyConfigurationDialog from './dialogs/CopyConfigurationDialog';
import FormTextField from '../common/FormTextField';
import { isValidEmail, isValidPhone } from '../../validation/fieldValidators';
import LinearProgress from '@mui/material/LinearProgress';
import BitFlagSelect from '../common/BitFlagSelect';
import TabWithError from '../common/TabWithError';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ImageIcon from '@mui/icons-material/Image';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import CropLandscapeIcon from '@mui/icons-material/CropLandscape';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { AppType } from '@/models/enums/AppType';
import { DeviceOrientation } from '@/models/enums/DeviceOrientation';
import { prettifyEnumName } from '@/models/enums/EnumHelper';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

// Which fields a draft save validates, grouped by the tab they live on (insertion order = tab-bar order, so
// the user is sent to the first tab that has a problem).
//
// EVERY rule enforced on save has to be listed here. The form is in react-hook-form's default `onSubmit`
// mode and submitDraft never calls handleSubmit — it validates this list and then saves getValues()
// directly, because a location saves partially by design (only Name is required; the rest is filled in over
// time, and completeness is enforced separately when enabling). The consequence is that a rule registered on
// a field but absent from this list is NEVER RUN: no error appears and the save goes through regardless.
// That is exactly how negative values reached the API from the Auth tab despite those fields carrying `min`
// rules (TTD-169). Adding a validated field to the screen means adding its path here too.
const DRAFT_VALIDATED_PATHS: Record<string, readonly string[]> = {
    // Name is the one genuinely required field. Email and phone are optional, but a bad FORMAT still blocks
    // (their rules no-op on an empty value, so a blank field saves fine).
    detail: ['name', 'email', 'phoneNumber'],
    // Optional, but must be >= 0 if entered.
    authentication: [
        'authenticationConfiguration.authenticationInactivityTimeoutSeconds',
        'authenticationConfiguration.pinCodeMinLength',
        'authenticationConfiguration.pinCodeMaxLength',
    ],
    // Digital wallet fields are required when their toggle is on (the rules live on the fields; they're
    // only enforced here). When a toggle is off the fields are unmounted, so trigger passes them.
    paymentConfig: [
        'paymentConfiguration.digitalWalletConfiguration.applePayMerchantId',
        'paymentConfiguration.digitalWalletConfiguration.applePayMerchantCertificate',
        'paymentConfiguration.digitalWalletConfiguration.googlePayMerchantId',
        'paymentConfiguration.digitalWalletConfiguration.googlePayMerchantName',
    ],
};

const LocationDetails = () => {
    const { slug } = useParams<{ slug: string }>();
    const isEditing = slug !== 'new';
    const [searchParams] = useSearchParams();
    const locationType = parseInt(searchParams.get("type") ?? "0");
    const brandId = searchParams.get("brandId");
    const organizationId = searchParams.get("orgId");
    const [tabValue, setTabValue] = useState<string>('detail');
    const [copyOpen, setCopyOpen] = useState(false);
    // Records provider sections just copied from another location so ProviderConfig's vendor-change effects
    // keep the copied configurationJson instead of overwriting it with a blank template. Cleared on save.
    const copiedConfigRef = useRef<Record<string, CopiedConfig>>({});
    // Bumped after a Copy completes so each provider tab re-applies the copied config authoritatively
    // (needed for field maps, and for same-vendor/version copies that don't trigger the vendor-change effect).
    const [copyNonce, setCopyNonce] = useState(0);
    const methods = useForm<Location>(!isEditing ? { defaultValues: createLocation(0, organizationId!, brandId!) } : undefined);
    const { control, reset, register, handleSubmit: onFormSubmit, formState, watch, getValues, setValue, trigger } = methods;
    // Prompt before leaving with unsaved edits on any tab (also covers copied-in config, which marks dirty).
    // Called further down, once navigate/app exist — the prompt needs to know how to leave this screen.

    // Apps tab: which apps this location uses (each with a default orientation)
    const licensedApps = watch('licensedApps') ?? [];
    // Not built yet — hidden from selection.
    const HIDDEN_APP_TYPES: AppType[] = [AppType.OrderAndPay];
    // Exactly one of these "primary" apps may be selected — they're mutually exclusive. GuestPayBYOD, PaySDK,
    // DualDisplay and Tabletop are independent add-ons that can accompany the selected primary.
    const PRIMARY_APP_TYPES: AppType[] = [AppType.Pay, AppType.OrderAndPayDriveThru, AppType.GuestPay];
    // DualDisplay is deliberately absent: it is always landscape, so offering a choice would imply one that
    // doesn't exist. Its orientation is set on selection instead, below.
    const APPS_WITH_ORIENTATION: AppType[] = [...PRIMARY_APP_TYPES, AppType.Tabletop];
    // Tabletop is mounted one way or the other, so Unspecified isn't a meaningful answer for it.
    const orientationOptionsFor = (appType: AppType): DeviceOrientation[] =>
        appType === AppType.Tabletop
            ? [DeviceOrientation.Portrait, DeviceOrientation.Landscape]
            : [DeviceOrientation.Unspecified, DeviceOrientation.Portrait, DeviceOrientation.Landscape];
    // What an app starts as when it's switched on. DualDisplay is fixed landscape and has no picker, so this
    // is the only place it gets set; Tabletop needs a real value because Unspecified isn't on offer.
    const defaultOrientationFor = (appType: AppType): DeviceOrientation =>
        appType === AppType.DualDisplay || appType === AppType.Tabletop
            ? DeviceOrientation.Landscape
            : DeviceOrientation.Unspecified;
    const isAppEnabled = (appType: AppType) => licensedApps.some(a => a.type === appType);
    const getAppOrientation = (appType: AppType) => licensedApps.find(a => a.type === appType)?.defaultOrientation ?? DeviceOrientation.Unspecified;
    const toggleApp = (type: AppType, enabled: boolean) => {
        if (!enabled) {
            setValue('licensedApps', licensedApps.filter(a => a.type !== type), { shouldDirty: true });
            return;
        }
        // Selecting a primary app clears any other primary (mutual exclusivity); add-ons are left untouched.
        const base = PRIMARY_APP_TYPES.includes(type)
            ? licensedApps.filter(a => !PRIMARY_APP_TYPES.includes(a.type))
            : licensedApps;
        setValue('licensedApps', [...base, { type, defaultOrientation: defaultOrientationFor(type) }], { shouldDirty: true });
    };
    const setAppOrientation = (appType: AppType, orientation: DeviceOrientation) =>
        setValue('licensedApps', licensedApps.map(a => a.type === appType ? { ...a, defaultOrientation: orientation } : a), { shouldDirty: true });
    //const methods = useForm<FormValues>();
    const currentCountry = watch("country");
    // PMS only applies to hotel venues, so the PMS tab is shown only when Hotel Venue is among the selected restaurant types.
    const restaurantTypes = watch("restaurant.types") ?? 0;
    const showPmsTab = (restaurantTypes & RestaurantType.HotelVenue) === RestaurantType.HotelVenue;
    // Authentication tab: which fields apply depends on the selected authentication provider.
    const authProvider = watch("authenticationConfiguration.authenticationProvider") ?? AuthenticationProvider.None;
    const showAuthFields = authProvider !== AuthenticationProvider.None;
    const showUsePosIdField = authProvider === AuthenticationProvider.PosViaTouchTab;
    const showPinCodeLengths = authProvider === AuthenticationProvider.PosViaTouchTab || authProvider === AuthenticationProvider.PosDirect;
    //const customTipButtonEnabled = watch("restaurant.enableCustomTipButton");
    //const qc = useQueryClient();

    // Always refetch on mount so entering the edit screen reflects the current DB row (id, slug, fields).
    // A stale cached location here would feed a stale id to the readiness/QR-code calls (which look up by
    // id) and 404. We still avoid focus/reconnect refetches so editing in place isn't clobbered.
    const { data: location, isPending } = useQuery<Location>({ queryKey: ['location', slug], enabled: isEditing, queryFn: () => locationApi.getBySlug(slug!), staleTime: 0, refetchOnWindowFocus: false, refetchOnReconnect: false, refetchOnMount: 'always' });
    const qc = useQueryClient();
    // Locale choices come from the BRAND's supported set, not the full list. Declaring the languages a brand
    // operates in is what makes this dropdown correct by construction - a location cannot be set to a language
    // its brand does not run in, and the content-strings screen shows exactly those columns.
    //
    // Falls back to the full list only while the brand is still loading, so the field is never empty and an
    // existing value never looks invalid mid-load.
    const effectiveBrandId = location?.brandId ?? brandId ?? undefined;
    const { data: brandForLocales } = useQuery<Brand>({
        queryKey: ['brand-for-locales', effectiveBrandId],
        enabled: !!effectiveBrandId,
        queryFn: () => brandApi.get(effectiveBrandId!),
    });

    const localeOptions = useMemo(() => {
        const supported = brandForLocales?.supportedLocales ?? [];
        if (supported.length === 0) return LOCALES;
        const allowed = new Set(supported.map(l => l.toLowerCase()));
        return LOCALES.filter(l => allowed.has(l.code.toLowerCase()));
    }, [brandForLocales]);
    // Completeness of the saved location — drives the Enabled toggle and the checklist of what's still missing.
    const { data: readiness, isFetching: readinessFetching } = useQuery<LocationReadiness>({ queryKey: ['location-readiness', location?.id ?? ''], enabled: isEditing && !!location?.id, queryFn: () => locationApi.getReadiness(location!.id!), staleTime: 0 });
    const { mutateAsync: saveLocation, isPending: isSaving } = useMutation({ mutationFn: locationApi.save, onSuccess: () => handleSaveSuccess(), onError: () => handleSaveError() });
    const [qrUrl, setQrUrl] = useState<string>();
    const [qrError, setQrError] = useState(false);
    const [reloadQrCode, setReloadQrCode] = useState(false);
    //const [defaultTip, setDefaultTip] = useState<number>(0);
    const toast = useToast();
    const alert = useAlert();
    const theme = useTheme();
    const strings = useLocalizedStrings();
    const canUpdate = usePermissions().canUpdate(Perm.adminConfiguration);
    const settleDiscard = useSettleDiscard(methods);
    const app = useAppContext();
    const navigate = useNavigate();

    // How this screen leaves itself when Leave is answered at the scope selector, which the router cannot
    // see. Without it the scope changed and this editor stayed open, still dirty, still showing a location
    // from the scope just left (TTD-149).
    //
    // An explicit route rather than the navigate(-1) that Cancel uses: after a scope change the previous
    // history entry may be this same editor, and the ids in the URL belong to the scope being left.
    const leaveToConfiguration = useCallback(
        () => navigate(`/${app.organizationSlug}/admin/configuration/organizations`),
        [navigate, app.organizationSlug]
    );
    useUnsavedChangesPrompt(formState.isDirty, leaveToConfiguration);

    const { data: currentBrand } = useCurrentBrand();
    // Loyalty is a brand-level opt-in: a location's Loyalty tab only applies when the brand has selected a
    // loyalty provider on the BrandDetails page. No brand loyalty provider => no location Loyalty tab.
    const showLoyaltyTab = !!currentBrand?.loyaltyConfiguration?.vendor;

    // Load selected location into the form when fetched
    useEffect(() => {
        app.setTitle(!isEditing ? strings.locationNewTitle : strings.locationEditTitle.replace("{{name}}", location?.name ?? ""));
        reset(location);
    }, [location, reset]);

    // If a conditionally-shown tab (PMS, Loyalty) becomes hidden while it's active, fall back to detail.
    useEffect(() => {
        if (!showPmsTab && tabValue === 'pmsConfig') {
            setTabValue('detail');
        }
        if (!showLoyaltyTab && tabValue === 'loyaltyConfig') {
            setTabValue('detail');
        }
    }, [showPmsTab, showLoyaltyTab, tabValue]);

    useEffect(() => {
        const id = location?.id;
        if (!id) return;

        let cancelled = false;
        let objectUrl: string | undefined;
        setQrError(false);

        // A just-created location can 404 here: the create writes to the primary, but this read can hit a
        // SQL read replica that hasn't caught up yet (read-after-write lag). So retry with a short backoff
        // before giving up, and never let a failure throw — fall back to a small error+retry state instead.
        const load = async () => {
            const delaysMs = [0, 600, 1200, 2400, 4000];
            for (const delay of delaysMs) {
                if (delay) await new Promise((r) => setTimeout(r, delay));
                if (cancelled) return;
                try {
                    const url = await locationApi.getQrCode(id);
                    if (cancelled) { URL.revokeObjectURL(url); return; }
                    objectUrl = url;
                    setQrUrl(url);
                    return;
                } catch {
                    // transient (likely replica lag) — keep retrying
                }
            }
            if (!cancelled) setQrError(true);
        };

        load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [location?.id, reloadQrCode]);

    const handleSubmit: SubmitHandler<Location> = async (data) => {
        // A config counts as "set" once a vendor is chosen (matches the backend gate). externalId and the
        // rest are filled in later (e.g. during client onboarding), so we must persist a vendor-only config.
        const cleaned: Location = {
            ...data,
            posConfiguration: data.posConfiguration?.vendor ? data.posConfiguration : null,
            // Integrations nobody filled in are dropped rather than posted as blank templates. The vendor API
            // enforces required fields on anything it is sent that looks configured, and a blank AFCC template
            // looks configured to it because of its numeric/boolean defaults — which made choosing FreedomPay
            // impossible (TTD-168). See untouchedConfig for why only strings count.
            paymentConfiguration: data.paymentConfiguration?.vendor ? withConfiguredIntegrationsOnly(data.paymentConfiguration) : null,
            pmsConfiguration: data.pmsConfiguration?.vendor ? data.pmsConfiguration : null,
            loyaltyConfiguration: data.loyaltyConfiguration?.vendor ? data.loyaltyConfiguration : null,
            feedbackConfiguration: data.feedbackConfiguration?.vendor ? data.feedbackConfiguration : null
        };
        await saveLocation(cleaned);
    }

    // Save a partial location: validate only the rules a draft has to satisfy (see DRAFT_VALIDATED_PATHS
    // above for why the list is explicit and what happens to anything left off it).
    const submitDraft = async (e: React.FormEvent) => {
        e.preventDefault();
        // Validate tab by tab, in tab-bar order, and stop on the first tab that fails so the user is taken
        // to the field that blocked them rather than left staring at a button that did nothing.
        for (const [tab, paths] of Object.entries(DRAFT_VALIDATED_PATHS)) {
            if (!(await trigger(paths as unknown as Parameters<typeof trigger>[0]))) {
                setTabValue(tab);
                return;
            }
        }
        await handleSubmit(getValues());
    };

    const handleInvalid = (errors: any) => {
        console.log("submit invalid", errors);

        // Jump to the first tab (in tab-bar order) that contains a validation error.
        const detailKeys = ['name', 'address1', 'address2', 'city', 'country', 'state', 'postalCode',
            'timeZoneId', 'phoneNumber', 'email', 'currency', 'locale', 'salesforceId', 'enabled'];
        const tabOrder: { tab: string; hasError: () => boolean }[] = [
            { tab: 'detail', hasError: () => detailKeys.some(k => !!errors[k]) },
            { tab: 'apps', hasError: () => !!errors.licensedApps },
            { tab: 'restaurant', hasError: () => !!errors.restaurant },
            { tab: 'authentication', hasError: () => !!errors.authenticationConfiguration },
            { tab: 'paymentConfig', hasError: () => !!errors.paymentConfiguration },
            { tab: 'posConfig', hasError: () => !!errors.posConfiguration },
            { tab: 'pmsConfig', hasError: () => !!errors.pmsConfiguration },
            { tab: 'loyaltyConfig', hasError: () => !!errors.loyaltyConfiguration },
            { tab: 'feedbackConfig', hasError: () => !!errors.feedbackConfiguration },
        ];
        const firstErrored = tabOrder.find(t => t.hasError());
        if (firstErrored) setTabValue(firstErrored.tab);

        // Switching tabs is not on its own an explanation - the user is left to spot the red field and work
        // out that it was the thing blocking the save.
        toast({ severity: 'error', message: strings.validationRequiredFieldsMissing });
    };

    // This is probably a better way to clean the config data above
    //function nullIfAllNull<T extends object | null>(obj: T): T | null {
    //    if (!obj) return null;

    //    const hasValue = Object.values(obj).some(
    //        (v) => v !== null && v !== undefined && v !== ""
    //    );

    //    return hasValue ? obj : null;
    //}

    const handleSaveSuccess = () => {
        toast({ severity: "success", message: strings.locationSaveSuccess });
        // Save in place: clear the dirty flag now so Save re-disables immediately.
        reset(getValues());
        // The copied config is now the saved baseline (defaultValues), so drop the copy markers — further
        // vendor switches should use the normal saved-restore/template logic.
        copiedConfigRef.current = {};

        // Refresh the location list + onboarding list (vendor/version changes move a location in or out of
        // onboarding), and this location + its readiness checklist so they reflect what was just saved.
        //
        // Deliberately NOT awaited, and this function is deliberately not async: react-query awaits whatever
        // onSuccess returns, so awaiting these would keep the mutation 'pending' — spinning the Save
        // button — until every refetch finished. Readiness alone fans out to the POS/Payment/Loyalty
        // validate calls (and retries on failure), which can take a long time. The refetches still happen;
        // they just don't hold the button hostage.
        qc.invalidateQueries({ queryKey: ['locations'] });
        qc.invalidateQueries({ queryKey: ['onboarding-pending'] });
        qc.invalidateQueries({ queryKey: ['location', slug] });
        qc.invalidateQueries({ queryKey: ['location-readiness'] });
    }

    const handleSaveError = () => {
        toast({ severity: "error", message: strings.locationSaveError });
    }

    // The onboarding-email button only applies while a location is still being onboarded: it disappears
    // once the location has ever been enabled (firstEnabledAt is set and never cleared, so a re-disabled
    // location stays "onboarded" — same rule as the onboarding grid).
    const showOnboardingEmail = !!location && !location.firstEnabledAt;

    // Enables only once the SAVED location has a POS and Payment vendor+version (name is implicit — a
    // location can't be saved without it), AND the brand has an onboarding email set (that's where the
    // email is sent from/to). Reads the loaded `location`, not the live form, so unsaved edits don't
    // enable it: the user must save first (which is also what lands the location in the onboarding grid).
    const canSendOnboardingEmail =
        !!location?.posConfiguration?.vendor && !!location?.posConfiguration?.version &&
        !!location?.paymentConfiguration?.vendor && !!location?.paymentConfiguration?.version &&
        !!currentBrand?.onboardingEmail?.trim();

    const [sendingOnboardingEmail, setSendingOnboardingEmail] = useState(false);
    const handleSendOnboardingEmail = async () => {
        if (!location?.id) return;

        // If an onboarding email was already sent, confirm the resend and show when it last went out.
        if (location.onboardingEmailSentAt) {
            const sentOn = new Date(location.onboardingEmailSentAt).toLocaleString();
            const agree = await alert({
                title: strings.onboardingEmailResendTitle,
                message: strings.onboardingEmailResendMessage.replace("{{date}}", sentOn),
                agreeButtonTitle: strings.onboardingEmailResendConfirm,
                closeButtonTitle: strings.cancel,
            });
            if (!agree) return;
        }

        setSendingOnboardingEmail(true);
        try {
            await locationApi.sendOnboardingEmail(location.id);
            toast({ severity: "success", message: strings.onboardingEmailSent });
            qc.invalidateQueries({ queryKey: ['location', slug] });
        } catch {
            toast({ severity: "error", message: strings.onboardingEmailSendError });
        } finally {
            setSendingOnboardingEmail(false);
        }
    };

    // Enabled toggle. On the FIRST enable (firstEnabledAt not yet set), if a loyalty and/or feedback vendor
    // hasn't been selected, confirm before turning it on — loyalty/feedback are optional, so this is a
    // heads-up, not a block. Turning off, re-enabling later, or having both vendors set applies immediately.
    const handleEnabledToggle = async (checked: boolean, onChange: (value: boolean) => void) => {
        if (!checked || location?.firstEnabledAt) {
            onChange(checked);
            return;
        }

        // Only warn about a missing loyalty vendor when loyalty actually applies (brand has a provider).
        const loyaltyMissing = showLoyaltyTab && !getValues('loyaltyConfiguration')?.vendor;
        const feedbackMissing = !getValues('feedbackConfiguration')?.vendor;
        if (!loyaltyMissing && !feedbackMissing) {
            onChange(true);
            return;
        }

        const message = loyaltyMissing && feedbackMissing
            ? strings.enableMissingVendorsBoth
            : loyaltyMissing
                ? strings.enableMissingVendorsLoyalty
                : strings.enableMissingVendorsFeedback;

        const agree = await alert({
            title: strings.enableMissingVendorsTitle,
            message,
            agreeButtonTitle: strings.enableLocationConfirm,
            closeButtonTitle: strings.cancel,
        });

        // Only flip the switch on if they confirmed; otherwise leave it off.
        if (agree) onChange(true);
    };

    const handlePrintQrCodeClick = () => {
        const win = window.open("", "_blank");

        if (!win) return;

        win.document.write(`
            <html>
              <head>
                <title>${strings.print}</title>
                <style>
                  body { margin: 0; display: flex; justify-content: center; align-items: center; }
                  img { max-width: 100%; max-height: 100%; }
                </style>
              </head>
              <body>
                <img src="${qrUrl}" />
              </body>
            </html>
          `);

        win.document.close();
        win.focus();
        win.print();
    };

    const handleRegenQrCodeClick = async () => {
        const agree = await alert({
            title: strings.pairingCodeRegenConfrmTitle,
            message: strings.pairingCodeRegenConfrmMessage,
            destructive: false,
            agreeButtonTitle: strings.regenerate,
            closeButtonTitle: strings.cancel,
        });

        if (agree) {
            setQrUrl(undefined);
            await locationApi.createQrCode(location!.id!);
            setReloadQrCode(prev => !prev);
        }
    }

    const getHealthScoreName = (score: string | null) =>
        score === "U"
            ? strings.unknownHealthScore
            : score ?? "Unknown";

    // This maps the component's errors on each tab
    const tabErrors = {
        detail: !!(
            formState.errors.name ||
            formState.errors.address1 ||
            formState.errors.city ||
            formState.errors.country ||
            formState.errors.state ||
            formState.errors.postalCode ||
            formState.errors.timeZoneId ||
            formState.errors.phoneNumber ||
            formState.errors.email ||
            formState.errors.currency ||
            formState.errors.locale ||
            formState.errors.salesforceId
        ),
        restaurant: !!formState.errors.restaurant,
    } as const;

    // Readiness section names come from the PortalAPI aggregator and line up 1:1 with tabs.
    const readinessTabByName: Record<string, string> = {
        Details: 'detail',
        Apps: 'apps',
        Restaurant: 'restaurant',
        POS: 'posConfig',
        Payment: 'paymentConfig',
        Loyalty: 'loyaltyConfig',
        Feedback: 'feedbackConfig',
    };
    const notReadySections = (readiness?.sections ?? []).filter((s) => !s.ready);
    const incompleteTabs = new Set(notReadySections.map((s) => readinessTabByName[s.name]).filter(Boolean));
    const remainingCount = notReadySections.reduce((n, s) => n + s.missing.length, 0);
    const enabledValue = useWatch({ control, name: 'enabled' });
    // An enabled location that no longer passes its checks. Editing one can remove something it needed — an
    // app, a POS setting — and the status simply read "Enabled" in green, because being enabled was taken as
    // proof of being complete. The checklist was already computed and sitting in the tooltip underneath, but
    // nobody hovers a green chip (TTD-152).
    //
    // Readiness is the SERVER's verdict on the SAVED location, so this appears once the save has been made
    // rather than while typing. That is the honest moment: it reflects what the location actually is now.
    const enabledButIncomplete = !!enabledValue && !!readiness && !readiness.ready;

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden' }}>
            <FormProvider {...methods}>
                {/* noValidate: the required (*) markers are informational — react-hook-form owns validation
                    and the location saves incrementally (only Name is enforced). Without this, the browser's
                    native required-field check would block a partial save. */}
                <form noValidate onSubmit={submitDraft} id="location-form" style={{ height: '100%' }}>
                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: (theme) => theme.palette.background.paper, display: 'flex', alignItems: 'center' }}>
                            <TabList onChange={(_event, newValue) => setTabValue(newValue)} aria-label="tabs"
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 48,
                                    '& .MuiTab-root': {
                                        minHeight: 48,
                                    }
                                }}
                            >
                                <TabWithError icon={<InfoIcon />} iconPosition="start" label={strings.details} value="detail" hasError={tabErrors.detail} incomplete={incompleteTabs.has('detail')} />
                                <Tab icon={<QrCodeIcon />} iconPosition="start" label={strings.pairingCode} value="qrCode" />
                                <TabWithError icon={<PhoneAndroidIcon />} iconPosition="start" label={strings.apps} value="apps" incomplete={incompleteTabs.has('apps')} />
                                {locationType === 0 &&
                                    <TabWithError icon={<RestaurantIcon />} iconPosition="start" label={strings.restaurant} value="restaurant" hasError={tabErrors.restaurant} incomplete={incompleteTabs.has('restaurant')} />
                                }
                                {locationType === 1 &&
                                    <Tab icon={<ShoppingCartIcon />} iconPosition="start" label={strings.retail} value="retail" />
                                }
                                {locationType === 2 &&
                                    <Tab icon={<LocalShippingIcon />} iconPosition="start" label={strings.fieldService} value="service" />
                                }
                                {/*<Tab icon={<SmsIcon />} iconPosition="start" label={strings.sms} value="sms" />*/}
                                <Tab icon={<VpnKeyIcon />} iconPosition="start" label={strings.auth} value="authentication" />
                                <TabWithError icon={<CreditCardIcon />} iconPosition="start" label={strings.payment} value="paymentConfig" incomplete={incompleteTabs.has('paymentConfig')} />
                                <TabWithError icon={<PointOfSaleIcon />} iconPosition="start" label={strings.pos} value="posConfig" incomplete={incompleteTabs.has('posConfig')} />
                                {/*<Tab icon={<ImageIcon />} iconPosition="start" label={strings.images} value="imagesConfig" />*/}
                                {/*{locationType === 0 &&*/}
                                {/*    <Tab icon={<FastfoodIcon />} iconPosition="start" label={strings.nutrition} value="nutritionConfig" />*/}
                                {/*}*/}
                                {showPmsTab &&
                                    <Tab icon={<HotelIcon />} iconPosition="start" label={strings.pms} value="pmsConfig" />
                                }
                                {showLoyaltyTab &&
                                    <TabWithError icon={<FavoriteIcon />} iconPosition="start" label={strings.loyalty} value="loyaltyConfig" incomplete={incompleteTabs.has('loyaltyConfig')} />
                                }
                                <TabWithError icon={<ThumbUpAltIcon />} iconPosition="start" label={strings.feedback} value="feedbackConfig" incomplete={incompleteTabs.has('feedbackConfig')} />
                            </TabList>
            {/* Readiness fans out to the POS/Payment/Loyalty validate calls, so it can lag a save by a few
                            seconds. Show a "Validating…" chip while it's in flight (the tab dots are recomputing) so
                            the stale status/dots don't read as final. */}
                            {isEditing && (
                                readinessFetching ? (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        icon={<CircularProgress size={14} color="inherit" />}
                                        label={strings.locationValidating}
                                        sx={{ mx: 1, flexShrink: 0 }}
                                    />
                                ) : readiness ? (
                                    <Tooltip
                                        arrow
                                        title={notReadySections.length > 0 ? (
                                            <Box>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                    {enabledButIncomplete ? strings.locationEnabledIncompleteChecklist : strings.locationEnableChecklist}
                                                </Typography>
                                                {notReadySections.map((s) => (
                                                    <Typography key={s.name} variant="body2">• {s.name}: {s.missing.join(', ')}</Typography>
                                                ))}
                                            </Box>
                                        ) : ''}
                                    >
                                        <Chip
                                            size="small"
                                            // Being enabled used to win outright here, so a location that had
                                            // stopped meeting its requirements still showed a green "Enabled".
                                            color={enabledButIncomplete ? 'warning' : enabledValue ? 'success' : readiness.ready ? 'success' : 'warning'}
                                            variant={enabledValue ? 'filled' : 'outlined'}
                                            label={
                                                enabledButIncomplete
                                                    ? strings.locationEnabledIncomplete.replace('{{count}}', String(remainingCount))
                                                    : enabledValue
                                                        ? strings.enabled
                                                        : readiness.ready
                                                            ? strings.locationReadyToEnable
                                                            : strings.locationDraftRemaining.replace('{{count}}', String(remainingCount))
                                            }
                                            sx={{ mx: 1, flexShrink: 0 }}
                                        />
                                    </Tooltip>
                                ) : null
                            )}
                        </Box>
                        {isEditing && isPending ? (<LinearProgress />) :
                            <Box sx={{ height: '100%' }}>
                                <TabPanel id="detail" value="detail" keepMounted>
                                    <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                                        <Box>
                                            <FormTextField
                                                id="name"
                                                label={strings.name}
                                                variant="outlined"
                                                required
                                                sx={{ width: 600 }}
                                                {...register("name", {
                                                    required: strings.validationNameRequired
                                                })}
                                                isEditing={isEditing}
                                                error={!!formState.errors.name}
                                                helperText={formState.errors.name?.message || " "}
                                            />
                                            <Spacer />
                                            <FormTextField
                                                id="address-1"
                                                label={strings.address1}
                                                variant="outlined"
                                                required
                                                sx={{ width: 600 }}
                                                {...register("address1")}
                                                isEditing={isEditing}
                                                error={!!formState.errors.address1}
                                                helperText={formState.errors.address1?.message || " "}
                                            />
                                            <Spacer />
                                            <FormTextField
                                                id="address-2"
                                                label={strings.address2}
                                                variant="outlined"
                                                sx={{ width: 600 }}
                                                {...register("address2")}
                                                isEditing={isEditing}
                                                error={!!formState.errors.address2}
                                                helperText={formState.errors.address2?.message || " "}
                                            />
                                            <Spacer />
                                            <FormTextField
                                                id="city"
                                                label={strings.city}
                                                variant="outlined"
                                                required
                                                sx={{ width: 600 }}
                                                {...register("city")}
                                                isEditing={isEditing}
                                                error={!!formState.errors.city}
                                                helperText={formState.errors.city?.message || " "}
                                            />
                                            <Spacer />
                                            <Stack direction="row" spacing={2}>
                                                <FormControl required sx={{ width: `calc(250px - ${theme.spacing(2)})` }} error={!!formState.errors.country}>
                                                    <InputLabel id="country-label">{strings.country}</InputLabel>
                                                    <Controller
                                                        name="country"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                labelId="country-label"
                                                                id="country"
                                                                label={strings.country}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            >
                                                                {COUNTRIES.map((s) => (
                                                                    <MenuItem key={s.code} value={s.code}>
                                                                        {s.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                    />
                                                    <FormHelperText>{formState.errors.country?.message || " "}</FormHelperText>
                                                </FormControl>
                                                {currentCountry === "US" || currentCountry === "CA" ?
                                                    <FormControl required sx={{ width: 200 }} error={!!formState.errors.state}>
                                                        <InputLabel id="state-label">{currentCountry === "US" ? strings.state : strings.province}</InputLabel>
                                                        <Controller
                                                            name="state"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select
                                                                    labelId="state-label"
                                                                    id="state"
                                                                    label={currentCountry === "US" ? strings.state : strings.province}
                                                                    {...field}
                                                                    value={field.value ?? ""}
                                                                >
                                                                    {(currentCountry === "US" ? US_STATES : CANADIAN_PROVINCES).map((s) => (
                                                                        <MenuItem key={s.code} value={s.code}>
                                                                            {s.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            )}
                                                        />
                                                        <FormHelperText>{formState.errors.state?.message || " "}</FormHelperText>
                                                    </FormControl> :
                                                    <TextField
                                                        id="state-other"
                                                        label={strings.state}
                                                        variant="outlined"
                                                        required
                                                        sx={{ width: 200 }}
                                                        {...register("state")}
                                                        error={!!formState.errors.state}
                                                        helperText={formState.errors.state?.message || " "}
                                                    />
                                                }
                                                <FormTextField
                                                    id="zip-code"
                                                    label={currentCountry === "US" ? strings.zipCode : strings.postalCode}
                                                    variant="outlined"
                                                    required
                                                    sx={{ width: `calc(150px - ${theme.spacing(2)})` }}
                                                    {...register("postalCode")}
                                                    isEditing={isEditing}
                                                    error={!!formState.errors.postalCode}
                                                    helperText={formState.errors.postalCode?.message || " "}
                                                />
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <FormControl required sx={{ width: 600 }} error={!!formState.errors.timeZoneId}>
                                                <InputLabel id="time-zone-label">{strings.timeZone}</InputLabel>
                                                <Controller
                                                    name="timeZoneId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            labelId="time-zone-label"
                                                            id="time-zone-id"
                                                            label={strings.timeZone}
                                                            {...field}
                                                            value={field.value ?? ""}
                                                        >
                                                            {TIME_ZONES.map((s) => (
                                                                <MenuItem key={s.id} value={s.id}>
                                                                    {s.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                                <FormHelperText>{formState.errors.timeZoneId?.message || " "}</FormHelperText>
                                            </FormControl>
                                            <Spacer />
                                            <Stack direction="row" spacing={2}>
                                                <FormTextField
                                                    id="phone-number"
                                                    label={strings.phoneNumber}
                                                    variant="outlined"
                                                    sx={{ width: `calc(200px - ${theme.spacing(1)})` }}
                                                    // Optional field: only validate the format once something is entered.
                                                    {...register("phoneNumber", {
                                                        validate: (v?: string) => !v?.trim() || isValidPhone(v) || strings.validationPhoneNumberInvalid,
                                                    })}
                                                    isEditing={isEditing}
                                                    error={!!formState.errors.phoneNumber}
                                                    helperText={formState.errors.phoneNumber?.message || strings.phoneNumberHint}
                                                />
                                                <FormTextField
                                                    id="email"
                                                    label={strings.email}
                                                    variant="outlined"
                                                    sx={{ width: `calc(400px - ${theme.spacing(1)})` }}
                                                    // Optional field: only validate the format once something is entered.
                                                    {...register("email", {
                                                        validate: (v?: string) => !v?.trim() || isValidEmail(v) || strings.validationEmailInvalid,
                                                    })}
                                                    isEditing={isEditing}
                                                    error={!!formState.errors.email}
                                                    helperText={formState.errors.email?.message || " "}
                                                />
                                            </Stack>
                                            <Spacer />
                                            <Stack direction="row" spacing={2}>
                                                <FormControl required sx={{ width: `calc(250px - ${theme.spacing(1)})` }} error={!!formState.errors.currency}>
                                                    <InputLabel id="currency-label">{strings.currency}</InputLabel>
                                                    <Controller
                                                        name="currency"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                labelId="currency-label"
                                                                id="currency"
                                                                label={strings.currency}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            >
                                                                {CURRENCIES.map((s) => (
                                                                    <MenuItem key={s.code} value={s.code}>
                                                                        {s.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                    />
                                                    <FormHelperText>{formState.errors.state?.message || " "}</FormHelperText>
                                                </FormControl>
                                                <FormControl required sx={{ width: `calc(350px - ${theme.spacing(1)})` }} error={!!formState.errors.locale}>
                                                    <InputLabel id="locale-label">{strings.locale}</InputLabel>
                                                    <Controller
                                                        name="locale"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                labelId="locale-label"
                                                                id="locale"
                                                                label={strings.locale}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            >
                                                                {localeOptions.map((s) => (
                                                                    <MenuItem key={s.code} value={s.code}>
                                                                        {s.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                    />
                                                    <FormHelperText>{formState.errors.state?.message || " "}</FormHelperText>
                                                </FormControl>
                                            </Stack>
                                            <Spacer />
                                            <FormTextField
                                                id="salesforceId"
                                                label={strings.salesforceId}
                                                variant="outlined"
                                                required
                                                sx={{ width: 600 }}
                                                {...register("salesforceId")}
                                                isEditing={isEditing}
                                                error={!!formState.errors.salesforceId}
                                                helperText={formState.errors.salesforceId?.message || " "}
                                            />
                                            <Spacer />
                                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                                <Box sx={{ pb: '22.61px' }}>
                                                    <FormControl size="input">
                                                        <FormControlLabel
                                                            label={strings.enabled}
                                                            control={
                                                                <Controller
                                                                    name="enabled"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Switch
                                                                            checked={field.value ?? false}
                                                                            // Can't turn on until the location is complete; can always turn off.
                                                                            disabled={!field.value && !(readiness?.ready ?? false)}
                                                                            onChange={(e) => handleEnabledToggle(e.target.checked, field.onChange)}
                                                                        />
                                                                    )}
                                                                />
                                                            }
                                                        />
                                                    </FormControl>
                                                </Box>
                                                {isEditing && readiness && !readiness.ready && (
                                                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, maxWidth: 380 }}>
                                                        <Typography variant="caption" color="text.secondary">{strings.locationEnableChecklist}</Typography>
                                                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                                            {notReadySections.map((s) => {
                                                                const tab = readinessTabByName[s.name];
                                                                const text = `• ${s.name}: ${s.missing.join(', ')}`;
                                                                // Sections that map to a tab are clickable; brand-level ones (e.g. Branding,
                                                                // managed on the Skinning page) have no location tab, so show them as plain text.
                                                                return tab ? (
                                                                    <Link
                                                                        key={s.name}
                                                                        component="button"
                                                                        type="button"
                                                                        variant="body2"
                                                                        underline="hover"
                                                                        onClick={() => setTabValue(tab)}
                                                                        sx={{ textAlign: 'left', display: 'block' }}
                                                                    >
                                                                        {text}
                                                                    </Link>
                                                                ) : (
                                                                    <Typography key={s.name} variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                                                        {text}
                                                                    </Typography>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </TabPanel>
                                <TabPanel id="qrCode" value="qrCode" keepMounted>
                                    {isEditing ?
                                        <>
                                            <Box sx={{
                                                width: 250,
                                                height: 250,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}>
                                                {location && qrUrl ? (
                                                    <img src={qrUrl} />
                                                ) : qrError ? (
                                                    <Stack spacing={1} alignItems="center">
                                                        <Typography variant="body2" color="textSecondary" textAlign="center">{strings.qrCodeLoadError}</Typography>
                                                        <Button size="small" startIcon={<RefreshIcon />} onClick={() => setReloadQrCode(prev => !prev)}>{strings.retry}</Button>
                                                    </Stack>
                                                ) : (
                                                    <CircularProgress />
                                                )}
                                            </Box>
                                            <Spacer y={2} />
                                            <Button
                                                id="print-qrcode-button"
                                                variant="contained"
                                                startIcon={<PrintIcon />}
                                                sx={{ width: 250 }}
                                                disabled={!qrUrl}
                                                onClick={handlePrintQrCodeClick}
                                            >
                                                {strings.print}
                                            </Button>
                                            <Spacer />
                                            <Button
                                                id="new-qrcode-button"
                                                variant="contained"
                                                startIcon={<RefreshIcon />}
                                                sx={{ width: 250 }}
                                                onClick={handleRegenQrCodeClick}
                                            >
                                                {strings.regenerate}
                                            </Button>
                                        </> :
                                        <Typography variant="body1" color="textSecondary">{strings.saveToGenerateQrCode}</Typography>
                                    }
                                </TabPanel>
                                <TabPanel id="apps" value="apps" keepMounted>
                                    <Box>
                                        <Typography variant="body1" fontWeight="700" color="textSecondary" sx={{ mb: 2 }}>{strings.licensedApps}</Typography>
                                        <Stack spacing={1}>
                                            {Object.entries(AppType)
                                                .filter(([, value]) => !HIDDEN_APP_TYPES.includes(value as AppType))
                                                .map(([name, value]) => {
                                                const appType = value as AppType;
                                                const enabled = isAppEnabled(appType);
                                                const showOrientation = enabled && APPS_WITH_ORIENTATION.includes(appType);
                                                return (
                                                    <Box key={appType}>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={enabled} onChange={(e) => toggleApp(appType, e.target.checked)} />}
                                                            label={prettifyEnumName(name)}
                                                        />
                                                        {showOrientation &&
                                                            <FormControl sx={{ display: 'block', ml: 4, mb: 1 }}>
                                                                <FormLabel>{strings.defaultTabletOrientation}</FormLabel>
                                                                <RadioGroup
                                                                    row
                                                                    value={getAppOrientation(appType).toString()}
                                                                    onChange={(e) => setAppOrientation(appType, Number(e.target.value) as DeviceOrientation)}
                                                                    sx={{ mt: 0.5 }}
                                                                >
                                                                    {Object.entries(DeviceOrientation)
                                                                        .filter(([, ovalue]) => orientationOptionsFor(appType).includes(Number(ovalue) as DeviceOrientation))
                                                                        .map(([oname, ovalue]) => {
                                                                        const ov = Number(ovalue);
                                                                        const icon = ov === DeviceOrientation.Portrait ? <CropPortraitIcon sx={{ fontSize: 36 }} />
                                                                            : ov === DeviceOrientation.Landscape ? <CropLandscapeIcon sx={{ fontSize: 36 }} />
                                                                            : <CropFreeIcon sx={{ fontSize: 36 }} />;
                                                                        return (
                                                                            <Stack key={ov} alignItems="center" spacing={0.5}>
                                                                                {icon}
                                                                                <FormControlLabel
                                                                                    value={ov.toString()}
                                                                                    control={<Radio size="small" />}
                                                                                    label={prettifyEnumName(oname)}
                                                                                    sx={{ m: 0, '& .MuiFormControlLabel-label': { minWidth: 90 } }}
                                                                                />
                                                                            </Stack>
                                                                        );
                                                                    })}
                                                                </RadioGroup>
                                                            </FormControl>
                                                        }
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </TabPanel>
                                {locationType === 0 &&
                                    <TabPanel id="restaurant" value="restaurant" keepMounted>
                                        <Box>
                                            <FormControl sx={{ width: 420 }} error={!!formState.errors.restaurant?.types}>
                                                <InputLabel id="restaurant-type-label">{strings.type}</InputLabel>
                                                <Controller
                                                    name="restaurant.types"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <BitFlagSelect
                                                            labelId="restaurant-type-label"
                                                            id="restaurant-type"
                                                            label={strings.type}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            options={Object.entries(RestaurantType)
                                                                .filter(([, value]) => value !== RestaurantType.None)
                                                                .map(([name, value]) => ({ value, label: prettifyEnumName(name) }))}
                                                        />
                                                    )}
                                                />
                                                <FormHelperText>{formState.errors.restaurant?.types?.message || " "}</FormHelperText>
                                            </FormControl>
                                            {/* Tips management moved to Configuration -> Tips. Preserved here for reuse; delete once Configuration -> Tips is built.
                                            <Spacer />
                                            <Typography variant="body1" fontWeight="700" color="textSecondary" sx={{ mb: 1 }}>{strings.tips}</Typography>
                                            <Controller
                                                name="restaurant.defaultTip"
                                            control={control}
                                            render={({ field }) => (
                                                <Stack direction="row" spacing={2}>
                                                    <Stack direction="column">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={field.value === 1}
                                                                    onChange={() => field.onChange(1)}
                                                                />
                                                            }
                                                            label="Default"
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                        <FormTextField
                                                            id="tip-1"
                                                            label={strings.tipAmount1}
                                                            variant="outlined"
                                                            type="number"
                                                            {...register("restaurant.tipAmount1", { valueAsNumber: true })}
                                                            isEditing={isEditing}
                                                            error={!!formState.errors.restaurant?.tipAmount1}
                                                            helperText={formState.errors.restaurant?.tipAmount1?.message || " "}
                                                        />
                                                    </Stack>
                                                    <Stack direction="column">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={field.value === 2}
                                                                    onChange={() => field.onChange(2)}
                                                                />
                                                            }
                                                            label="Default"
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                        <FormTextField
                                                            id="tip-2"
                                                            label={strings.tipAmount2}
                                                            variant="outlined"
                                                            type="number"
                                                            {...register("restaurant.tipAmount2", { valueAsNumber: true })}
                                                            isEditing={isEditing}
                                                            error={!!formState.errors.restaurant?.tipAmount2}
                                                            helperText={formState.errors.restaurant?.tipAmount2?.message || " "}
                                                        />
                                                    </Stack>
                                                    <Stack direction="column">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={field.value === 3}
                                                                    onChange={() => field.onChange(3)}
                                                                />
                                                            }
                                                            label="Default"
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                        <FormTextField
                                                            id="tip-3"
                                                            label={strings.tipAmount3}
                                                            variant="outlined"
                                                            type="number"
                                                            {...register("restaurant.tipAmount3", { valueAsNumber: true })}
                                                            isEditing={isEditing}
                                                            error={!!formState.errors.restaurant?.tipAmount3}
                                                            helperText={formState.errors.restaurant?.tipAmount3?.message || " "}
                                                        />
                                                    </Stack>
                                                </Stack>
                                            )}
                                        />
                                        <Spacer />
                                        <Typography variant="body1" fontWeight="700" color="textSecondary" sx={{ mb: 2 }}>{strings.tipGroupTitle}</Typography>
                                        <Stack direction="row" spacing={2}>
                                            <FormTextField
                                                id="min-tip-guest-count"
                                                label={strings.tipGroupCount}
                                                variant="outlined"
                                                type="number"
                                                {...register("restaurant.guestCountMinimumTip", { valueAsNumber: true })}
                                                isEditing={isEditing}
                                                error={!!formState.errors.restaurant?.guestCountMinimumTip}
                                                helperText={formState.errors.restaurant?.guestCountMinimumTip?.message || " "}
                                            />
                                            <FormTextField
                                                id="min-tip"
                                                label={strings.tipGroupAmount}
                                                variant="outlined"
                                                type="number"
                                                {...register("restaurant.minimumTipAmount", { valueAsNumber: true })}
                                                isEditing={isEditing}
                                                error={!!formState.errors.restaurant?.minimumTipAmount}
                                                helperText={formState.errors.restaurant?.minimumTipAmount?.message || " "}
                                            />
                                        </Stack>
                                        */}
                                        </Box>
                                    </TabPanel>
                                }
                                {locationType === 1 &&
                                    <TabPanel id="retail" value="retail" keepMounted>
                                    </TabPanel>
                                }
                                {locationType === 2 &&
                                    <TabPanel id="service" value="service" keepMounted>
                                    </TabPanel>
                                }
                                <TabPanel id="authentication" value="authentication" keepMounted sx={{ height: '100%', padding: 0 }}>
                                    <Box sx={{ p: 3 }}>
                                        <Stack spacing={2} sx={{ maxWidth: 400 }}>
                                            <FormControl fullWidth>
                                                <InputLabel id="auth-provider-label">{strings.authenticationProvider}</InputLabel>
                                                <Controller
                                                    name="authenticationConfiguration.authenticationProvider"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            labelId="auth-provider-label"
                                                            id="auth-provider"
                                                            label={strings.authenticationProvider}
                                                            {...field}
                                                            value={field.value ?? AuthenticationProvider.None}
                                                            onChange={(e) => {
                                                                const next = Number(e.target.value);
                                                                field.onChange(next);
                                                                // Clear fields that no longer apply to the selected provider.
                                                                if (next !== AuthenticationProvider.PosViaTouchTab) {
                                                                    setValue("authenticationConfiguration.usePosIdFieldForAuthentication", false, { shouldDirty: true });
                                                                }
                                                                if (next !== AuthenticationProvider.PosViaTouchTab && next !== AuthenticationProvider.PosDirect) {
                                                                    setValue("authenticationConfiguration.pinCodeMinLength", undefined, { shouldDirty: true });
                                                                    setValue("authenticationConfiguration.pinCodeMaxLength", undefined, { shouldDirty: true });
                                                                }
                                                            }}
                                                        >
                                                            {Object.entries(AuthenticationProvider).map(([name, value]) => (
                                                                <MenuItem key={value} value={value}>{prettifyEnumName(name)}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                            {showAuthFields &&
                                                <>
                                                    {/* min on the input stops the spinner arrows going below zero;
                                                        the rule stops a negative being TYPED, which the input
                                                        alone does not (react-hook-form does its own validation
                                                        and never consults the browser's) — TTD-169. */}
                                                    <FormTextField
                                                        id="authenticationInactivityTimeoutSeconds"
                                                        label={strings.authenticationInactivityTimeoutSeconds}
                                                        variant="outlined"
                                                        type="number"
                                                        sx={{ width: 400 }}
                                                        slotProps={{ htmlInput: { min: 0, step: 1 } }}
                                                        {...register("authenticationConfiguration.authenticationInactivityTimeoutSeconds", { valueAsNumber: true, min: { value: 0, message: strings.validationNonNegative } })}
                                                        isEditing={isEditing}
                                                        error={!!formState.errors.authenticationConfiguration?.authenticationInactivityTimeoutSeconds}
                                                        helperText={formState.errors.authenticationConfiguration?.authenticationInactivityTimeoutSeconds?.message || " "}
                                                    />
                                                    {showPinCodeLengths &&
                                                        <Stack direction="row" spacing={2}>
                                                            <FormTextField
                                                                id="pinCodeMinLength"
                                                                label={strings.pinCodeMinLength}
                                                                variant="outlined"
                                                                type="number"
                                                                sx={{ width: 200 }}
                                                                slotProps={{ htmlInput: { min: 0, step: 1 } }}
                                                                {...register("authenticationConfiguration.pinCodeMinLength", { valueAsNumber: true, min: { value: 0, message: strings.validationNonNegative } })}
                                                                isEditing={isEditing}
                                                                error={!!formState.errors.authenticationConfiguration?.pinCodeMinLength}
                                                                helperText={formState.errors.authenticationConfiguration?.pinCodeMinLength?.message || " "}
                                                            />
                                                            <FormTextField
                                                                id="pinCodeMaxLength"
                                                                label={strings.pinCodeMaxLength}
                                                                variant="outlined"
                                                                type="number"
                                                                sx={{ width: 200 }}
                                                                slotProps={{ htmlInput: { min: 0, step: 1 } }}
                                                                {...register("authenticationConfiguration.pinCodeMaxLength", { valueAsNumber: true, min: { value: 0, message: strings.validationNonNegative } })}
                                                                isEditing={isEditing}
                                                                error={!!formState.errors.authenticationConfiguration?.pinCodeMaxLength}
                                                                helperText={formState.errors.authenticationConfiguration?.pinCodeMaxLength?.message || " "}
                                                            />
                                                        </Stack>
                                                    }
                                                    {showUsePosIdField &&
                                                        <FormControl>
                                                            <FormControlLabel
                                                                label={strings.usePosIdFieldForAuthentication}
                                                                control={
                                                                    <Controller
                                                                        name="authenticationConfiguration.usePosIdFieldForAuthentication"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <Switch checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} />
                                                                        )}
                                                                    />
                                                                }
                                                            />
                                                        </FormControl>
                                                    }
                                                </>
                                            }
                                        </Stack>
                                    </Box>
                                </TabPanel>
                                <TabPanel id="posConfig" value="posConfig" keepMounted sx={{ height: '100%', padding: 0 }}>
                                    <ProviderConfig kind={0} isEditing={isEditing} copiedConfigRef={copiedConfigRef} copyNonce={copyNonce} />
                                </TabPanel>
                                {/*<TabPanel id="imagesConfig" value="imagesConfig" keepMounted sx={{ height: '100%', padding: 0 }}>*/}
                                {/*    <ProviderConfig kind={5} isEditing={isEditing} />*/}
                                {/*</TabPanel>*/}
                                {/*<TabPanel id="nutritionConfig" value="nutritionConfig" keepMounted sx={{ height: '100%', padding: 0 }}>*/}
                                {/*    <ProviderConfig kind={6} isEditing={isEditing} />*/}
                                {/*</TabPanel>*/}
                                <TabPanel id="paymentConfig" value="paymentConfig" keepMounted sx={{ height: '100%', padding: 0 }}>
                                    <ProviderConfig kind={1} isEditing={isEditing} copiedConfigRef={copiedConfigRef} copyNonce={copyNonce} />
                                </TabPanel>
                                {showPmsTab &&
                                    <TabPanel id="pmsConfig" value="pmsConfig" keepMounted sx={{ height: '100%', padding: 0 }}>
                                        <ProviderConfig kind={2} isEditing={isEditing} copiedConfigRef={copiedConfigRef} copyNonce={copyNonce} />
                                    </TabPanel>
                                }
                                {showLoyaltyTab &&
                                    <TabPanel id="loyaltyConfig" value="loyaltyConfig" keepMounted sx={{ height: '100%', padding: 0 }}>
                                        <ProviderConfig kind={3} isEditing={isEditing} copiedConfigRef={copiedConfigRef} copyNonce={copyNonce} />
                                    </TabPanel>
                                }
                                <TabPanel id="feedbackConfig" value="feedbackConfig" keepMounted sx={{ height: '100%', padding: 0 }}>
                                    <ProviderConfig kind={4} isEditing={isEditing} copiedConfigRef={copiedConfigRef} copyNonce={copyNonce} />
                                </TabPanel>
                            </Box>
                        }
                    </TabContext>
                </form>
                <CopyConfigurationDialog
                    open={copyOpen}
                    brandId={location?.brandId ?? brandId ?? undefined}
                    currentLocationId={location?.id}
                    locationType={locationType}
                    onClose={() => setCopyOpen(false)}
                    copiedConfigRef={copiedConfigRef}
                    onCopied={() => setCopyNonce((n) => n + 1)}
                />
            </FormProvider>
            <SaveFooter
                readOnly={!canUpdate}
                formId="location-form"
                formIsDirty={formState.isDirty}
                saving={isSaving}
                saveButtonTitle={strings.save}
                cancelButtonTitle={strings.cancel}
                onCancel={() => { app.setTitle(strings.configuration); navigate(-1); }}
                // Cancel puts the form back and stays; Close (shown once clean) is what leaves (TTD-161).
                // settleDiscard is what makes "once clean" true: the reset alone leaves the form still
                // claiming changes that no field accounts for, which trapped the footer on Cancel.
                onDiscard={() => { reset(location); settleDiscard(); }}
                startContent={
                    <Stack direction="row" spacing={2}>
                        {/* Copy targets the current location and fetches/stamps configs by its id, so keep it
                            disabled until the location has finished loading (avoids copying onto an undefined id). */}
                        <Button onClick={() => setCopyOpen(true)} startIcon={<ContentCopyIcon />} variant="outlined" disabled={isEditing && !location?.id}>
                            {strings.copyConfigurations}
                        </Button>
                        {/* Hidden once the location has ever been enabled (no longer in onboarding).
                            While shown: when disabled, MUI sets pointer-events:none on the button, so a click
                            lands on the wrapping span instead — that's how we surface the "fill these out
                            first" message (a real disabled button would swallow the click). Tooltip covers hover. */}
                        {showOnboardingEmail && (
                            <Tooltip title={canSendOnboardingEmail ? '' : strings.onboardingEmailRequirements}>
                                <span onClick={canSendOnboardingEmail ? undefined : () => toast({ severity: "info", message: strings.onboardingEmailRequirements })}>
                                    <Button
                                        onClick={handleSendOnboardingEmail}
                                        disabled={!canSendOnboardingEmail}
                                        loading={sendingOnboardingEmail}
                                        startIcon={<EmailIcon />}
                                        variant="outlined"
                                    >
                                        {strings.sendOnboardingEmail}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                }
            />
        </Box>
    );
};

export default LocationDetails;




{/*
                        <Spacer />
                        <Typography variant="body1" fontWeight="700" color="textSecondary" sx={{ mb: 2 }}>{strings.tipAutoGratuity}</Typography>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                id="auto-charge-id-1"
                                label={strings.tipChargeId1}
                                variant="outlined"
                                {...register("restaurant.autoGratuityExternalServiceChargeId1")}
                                error={Boolean(formState.errors.restaurant?.autoGratuityExternalServiceChargeId1)}
                                helperText={formState.errors.restaurant?.autoGratuityExternalServiceChargeId1?.message || " "}
                            />
                            <TextField
                                id="auto-additional-tip-1"
                                label={strings.tipAdditionalAmt1}
                                variant="outlined"
                                type="number"
                                {...register("restaurant.autoGratuityAdditionalTipAmount1")}
                                error={Boolean(formState.errors.restaurant?.autoGratuityAdditionalTipAmount1)}
                                helperText={formState.errors.restaurant?.autoGratuityAdditionalTipAmount1?.message || " "}
                            />
                        </Stack>
                        <Spacer />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                id="auto-charge-id-2"
                                label={strings.tipChargeId2}
                                variant="outlined"
                                {...register("restaurant.autoGratuityExternalServiceChargeId2")}
                                error={Boolean(formState.errors.restaurant?.autoGratuityExternalServiceChargeId2)}
                                helperText={formState.errors.restaurant?.autoGratuityExternalServiceChargeId2?.message || " "}
                            />
                            <TextField
                                id="auto-additional-tip-2"
                                label={strings.tipAdditionalAmt2}
                                variant="outlined"
                                type="number"
                                {...register("restaurant.autoGratuityAdditionalTipAmount2")}
                                error={Boolean(formState.errors.restaurant?.autoGratuityAdditionalTipAmount2)}
                                helperText={formState.errors.restaurant?.autoGratuityAdditionalTipAmount2?.message || " "}
                            />
                        </Stack>
                        <Spacer />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                id="auto-charge-id-3"
                                label={strings.tipChargeId3}
                                variant="outlined"
                                {...register("restaurant.autoGratuityExternalServiceChargeId3")}
                                error={Boolean(formState.errors.restaurant?.autoGratuityExternalServiceChargeId3)}
                                helperText={formState.errors.restaurant?.autoGratuityExternalServiceChargeId3?.message || " "}
                            />
                            <TextField
                                id="auto-additional-tip-3"
                                label={strings.tipAdditionalAmt3}
                                variant="outlined"
                                type="number"
                                {...register("restaurant.autoGratuityAdditionalTipAmount3")}
                                error={Boolean(formState.errors.restaurant?.autoGratuityAdditionalTipAmount3)}
                                helperText={formState.errors.restaurant?.autoGratuityAdditionalTipAmount3?.message || " "}
                            />
                        </Stack>
                        <Spacer />
                        <Typography variant="body1" fontWeight="700" color="textSecondary" sx={{ mb: 2 }}>{strings.tipCustomButton}</Typography>
                        <Stack direction="row" spacing={2}>
                            <FormControl>
                                <FormControlLabel
                                    label={strings.enabled}
                                    sx={{ mt: "9px" }}
                                    control={
                                        <Controller
                                            name="restaurant.enableCustomTipButton"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                            )}
                                        />
                                    }
                                />
                            </FormControl>
                            <TextField
                                id="custom-button-text"
                                label={strings.tipCustomButtonText}
                                variant="outlined"
                                disabled={!customTipButtonEnabled}
                                {...register("restaurant.customTipButtonText")}
                                error={Boolean(formState.errors.restaurant?.customTipButtonText)}
                                helperText={formState.errors.restaurant?.customTipButtonText?.message || " "}
                            />
                            <TextField
                                id="custom-button-amount"
                                label={strings.tipCustomButtonAmount}
                                variant="outlined"
                                type="number"
                                disabled={!customTipButtonEnabled}
                                {...register("restaurant.customTipButtonAmount")}
                                error={Boolean(formState.errors.restaurant?.customTipButtonAmount)}
                                helperText={formState.errors.restaurant?.customTipButtonAmount?.message || " "}
                            />
                        </Stack>
                        */}
